import React, { useEffect, useMemo, useState } from "react";
import {
  Bell,
  CheckCheck,
  Clock,
  Inbox,
  ShieldCheck,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingId, setReadingId] = useState(null);

  const userId = localStorage.getItem("user_id");

  const fetchNotifications = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get(
        `/get_user_notifications/${userId}/`
      );

      setNotifications(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.error("Error fetching notifications:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Notifications",
        text:
          error.response?.data?.message ||
          "We couldn't load your notifications. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId) => {
    setReadingId(notifId);

    try {
      await api.get(
        `/mark_notification_as_read/${notifId}/`
      );

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notifId
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);

      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text:
          error.response?.data?.message ||
          "We couldn't mark this notification as read.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setReadingId(null);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [userId]);

  const unreadCount = useMemo(() => {
    return notifications.filter(
      (notification) => !notification.is_read
    ).length;
  }, [notifications]);

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-3xl p-10 shadow-xl shadow-slate-900/5">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
            <Bell size={28} />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Sign in to view notifications
          </h1>

          <p className="text-slate-500 leading-relaxed mb-8">
            Your account notifications, service updates, and important
            messages will appear here.
          </p>

          <a
            href="/signin"
            className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
          >
            Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <ShieldCheck size={15} />
            Account Activity
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Notifications
              </h1>

              <p className="text-slate-300 text-lg max-w-2xl">
                Stay updated on your account activity, orders, services,
                and important messages from Nexindi Tech.
              </p>
            </div>

            {!loading && (
              <div className="inline-flex items-center gap-3 bg-white/10 border border-white/10 backdrop-blur-md rounded-2xl px-5 py-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Bell size={18} className="text-blue-300" />
                </div>

                <div>
                  <p className="text-2xl font-bold">
                    {unreadCount}
                  </p>

                  <p className="text-xs text-slate-400">
                    Unread messages
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Notifications */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
            {/* Top bar */}
            <div className="flex items-center justify-between px-6 md:px-8 py-6 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Recent notifications
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Your latest account updates and messages.
                </p>
              </div>

              {!loading && notifications.length > 0 && (
                <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
                  <CheckCheck size={16} className="text-blue-600" />
                  {notifications.length} total
                </div>
              )}
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-6 md:p-8 space-y-4">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="p-5 rounded-2xl border border-slate-200 animate-pulse"
                  >
                    <div className="flex gap-4">
                      <div className="w-11 h-11 bg-slate-200 rounded-xl flex-shrink-0" />

                      <div className="flex-1">
                        <div className="h-4 bg-slate-200 rounded w-4/5 mb-3" />
                        <div className="h-3 bg-slate-200 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && notifications.length === 0 && (
              <div className="px-6 py-20 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                  <Inbox size={28} />
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  You're all caught up
                </h3>

                <p className="text-slate-500 max-w-md mx-auto leading-relaxed">
                  You don't have any notifications yet. New account,
                  order, service, and platform updates will appear here.
                </p>
              </div>
            )}

            {/* Notification List */}
            {!loading && notifications.length > 0 && (
              <div className="divide-y divide-slate-100">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`relative px-6 md:px-8 py-6 transition-all duration-300 ${
                      notif.is_read
                        ? "bg-white"
                        : "bg-blue-50/60"
                    }`}
                  >
                    {/* Unread accent */}
                    {!notif.is_read && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600" />
                    )}

                    <div className="flex gap-4">
                      {/* Icon */}
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          notif.is_read
                            ? "bg-slate-100 text-slate-500"
                            : "bg-blue-600 text-white"
                        }`}
                      >
                        <Bell size={18} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div>
                            {!notif.is_read && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 mb-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                                New
                              </span>
                            )}

                            <p
                              className={`leading-relaxed ${
                                notif.is_read
                                  ? "text-slate-600"
                                  : "text-slate-900 font-medium"
                              }`}
                            >
                              {notif.message}
                            </p>
                          </div>

                          {!notif.is_read && (
                            <button
                              onClick={() =>
                                markAsRead(notif.id)
                              }
                              disabled={
                                readingId === notif.id
                              }
                              className="flex-shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 transition"
                            >
                              <CheckCheck size={16} />

                              {readingId === notif.id
                                ? "Updating..."
                                : "Mark read"}
                            </button>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
                          <Clock size={13} />

                          {formatDate(notif.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}